import React, { Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';

import './styles/App.css';
import { ThemeProvider } from './context/ThemeContext';
import RouteMeta from './components/RouteMeta';
import Navbar from './components/Navbar';
import ScrollProgress from './components/ScrollProgress';

// The homepage contains the LCP element (Hero <h1>). It is imported eagerly so
// the hero renders in a single critical-bundle round-trip instead of: load
// main.js → then fetch the Home chunk → then fetch framer-motion. On slow
// mobile connections that waterfall was the dominant part of the 4.7s LCP.
// Below-the-fold sections (Projects/Tools/Contact) stay lazy-loaded.
import Home from './pages/Home';
import Portfolio from './pages/Portfolio';

const PageFallback = () => (
  <div className="w-full min-h-screen flex items-center justify-center bg-[#0a0a0a]">
    <div className="w-10 h-10 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Maps every acceptable (lower-cased) path to its canonical route. This makes
// section links case-insensitive — e.g. /Contact and /CONTACT both land on the
// Contact section instead of being swallowed by the catch-all and sent to "/".
const KNOWN_ROUTES = {
  '/': '/',
  '/portfolio': '/portfolio',
  '/about': '/about',
  '/projects': '/projects',
  '/contact': '/contact',
  '/skills': '/Skills',
  '/tools': '/Skills',
};

// Rendered inside the router: if the current path differs from its canonical
// form only by case, redirect to the canonical URL.
function CaseNormalizer() {
  const { pathname } = useLocation();
  if (pathname === '/') return null;

  const lower = pathname.toLowerCase();
  const canonical = KNOWN_ROUTES[lower];
  if (canonical && canonical !== pathname) {
    return <Navigate to={canonical} replace />;
  }
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <RouteMeta />
        <CaseNormalizer />
        <ScrollProgress />
        <Navbar />

        <main className="relative min-h-screen bg-[#0a0a0a] text-white overflow-x-clip selection:bg-white/20 selection:text-white transition-colors duration-400">
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route
                path="/"
                element={<Home />}
              />

              <Route
                path="/portfolio"
                element={<Portfolio />}
              />

              {/* Section routes load the homepage; each just opens at the top
                  (no automatic scrolling). */}
              <Route
                path="/about"
                element={<Home />}
              />

              <Route
                path="/projects"
                element={<Home />}
              />

              <Route
                path="/contact"
                element={<Home />}
              />

              <Route
                path="/Skills"
                element={<Home />}
              />

              {/* Redirect old /tools and /skills references to the real /Skills page */}
              <Route
                path="/tools"
                element={<Navigate to="/Skills" replace />}
              />
              <Route
                path="/skills"
                element={<Navigate to="/Skills" replace />}
              />

              {/* Catch-all: redirect unknown routes to the homepage.
                  Not included in the sitemap. */}
              <Route
                path="*"
                element={<Navigate to="/" replace />}
              />
            </Routes>
          </Suspense>
        </main>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;

